import { useEffect, useState, useRef } from "react";
import {
  registerTradeHistoryLineOverlay,
  createTradeHistoryLineOverlay,
  TradeHistoryData,
} from "../custom-overlays/tradeHistoryLineOverlay";
import { useFilterPositions } from "@/vybe-trader-client/trade-history/hooks/useFilterTrades";
import {
  VybeOrderType,
  type TradeOrder,
} from "@/vybe-trader-client/trade-history/types";
import { useChartSettingsStore } from "@/lib/chart-settings";

function overlayMapsEqual(
  a: Map<string, string>,
  b: Map<string, string>
): boolean {
  if (a.size !== b.size) return false;
  for (const [k, v] of a) {
    if (b.get(k) !== v) return false;
  }
  return true;
}

interface UseTradeHistoryLinesProps {
  chartInstanceRef: React.MutableRefObject<any>;
  selectedSymbol: { currency: string } | null;
  selectedTimeframe: string;
  onTradeClick?: (trade: TradeHistoryData) => void;
  onTradeDoubleClick?: (trade: TradeHistoryData) => void;
  showTradeHistory?: boolean;
}

const BUY_ORDER_TYPES = new Set<number>([
  VybeOrderType.Buy,
  VybeOrderType.BuyLimit,
  VybeOrderType.BuyStop,
  VybeOrderType.BuyStopLimit,
]);

function isBuyPosition(position: TradeOrder): boolean {
  return BUY_ORDER_TYPES.has(position.orderType);
}

/**
 * A position is "closed" when it has a meaningful (non-zero) closeTime.
 * We avoid the legacy `state === 0` check because `VybeOrderState.Started === 0`,
 * which never represents a closed MT history position.
 */
function isClosedPosition(p: TradeOrder): boolean {
  if (!p.closeTime || typeof p.closeTime !== "string") return false;
  if (p.closeTime.startsWith("0001-01-01")) return false;
  if (typeof p.closeTimestampUTC === "number" && p.closeTimestampUTC > 0) {
    return true;
  }
  const t = Date.parse(p.closeTime);
  return Number.isFinite(t) && t > 0;
}

function symbolMatches(positionSymbol: string, selectedSymbol: string): boolean {
  return (
    positionSymbol === selectedSymbol ||
    positionSymbol === `${selectedSymbol}.`
  );
}

function calcPips(p: TradeOrder, isBuy: boolean): number {
  if (!p.openPrice || !p.closePrice) return 0;
  const digits = p.digits || 5;
  const pipValue = Math.pow(10, -Math.max(digits - 1, 0));
  if (pipValue <= 0) return 0;
  const diff = isBuy
    ? p.closePrice - p.openPrice
    : p.openPrice - p.closePrice;
  return diff / pipValue;
}

function formatProfitDisplay(
  position: TradeOrder,
  isBuy: boolean,
  isPipsVisible: boolean
): string {
  if (isPipsVisible) {
    const pips = calcPips(position, isBuy);
    const sign = pips > 0 ? "+" : "";
    return `${sign}${pips.toFixed(1)}p`;
  }
  const profit = position.profit || 0;
  const sign = profit > 0 ? "+" : "";
  return `${sign}${profit.toFixed(2)}`;
}

export const useTradeHistoryLines = ({
  chartInstanceRef,
  selectedSymbol,
  selectedTimeframe,
  onTradeClick,
  onTradeDoubleClick,
  showTradeHistory = false,
}: UseTradeHistoryLinesProps) => {
  const [overlayIds, setOverlayIds] = useState<Map<string, string>>(new Map());

  const isPipsVisible = useChartSettingsStore(
    (state) => state.chartSettings.features.isPipsVisible
  );

  const currentSymbolRef = useRef(selectedSymbol?.currency);
  const cleanupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCleaningUpRef = useRef(false);
  const justResetRef = useRef(false);

  const historyPositions = useFilterPositions({
    symbol: selectedSymbol?.currency || "allsymbols",
    dateFilter: "all",
    customDateFrom: undefined,
    customDateTo: undefined,
  });

  useEffect(() => {
    registerTradeHistoryLineOverlay();
  }, []);

  useEffect(() => {
    currentSymbolRef.current = selectedSymbol?.currency;
  }, [selectedSymbol]);

  const cancelAllTimeouts = () => {
    if (cleanupTimeoutRef.current) {
      clearTimeout(cleanupTimeoutRef.current);
      cleanupTimeoutRef.current = null;
    }
    if (initTimeoutRef.current) {
      clearTimeout(initTimeoutRef.current);
      initTimeoutRef.current = null;
    }
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  };

  const removeAllTradeHistoryLines = () => {
    if (!chartInstanceRef.current) return;

    isCleaningUpRef.current = true;

    overlayIds.forEach((overlayId) => {
      try {
        chartInstanceRef.current.removeOverlay(overlayId);
      } catch (error) {
        console.error(`Failed to remove history overlay ${overlayId}:`, error);
      }
    });

    setOverlayIds(new Map());
    isCleaningUpRef.current = false;
  };

  const syncTradeHistoryLines = () => {
    if (!chartInstanceRef.current || !selectedSymbol || isCleaningUpRef.current)
      return;

    if (currentSymbolRef.current !== selectedSymbol.currency) {
      return;
    }

    const currentSymbol = selectedSymbol.currency;
    const currentOverlayIds = justResetRef.current
      ? new Map<string, string>()
      : new Map(overlayIds);
    const activeTickets = new Set<string>();

    const relevantPositions = historyPositions.filter((position) => {
      if (!symbolMatches(position.symbol, currentSymbol)) return false;
      if (!isClosedPosition(position)) return false;
      if (typeof position.openPrice !== "number" || position.openPrice <= 0) {
        return false;
      }
      if (typeof position.closePrice !== "number" || position.closePrice <= 0) {
        return false;
      }
      return true;
    });

    relevantPositions.forEach((position) => {
      if (currentSymbolRef.current !== currentSymbol) return;

      const ticketKey = position.ticket.toString();
      activeTickets.add(ticketKey);

      const openTimeMs = new Date(position.openTime).getTime();
      const closeTimeMs = new Date(position.closeTime).getTime();
      if (!Number.isFinite(openTimeMs) || !Number.isFinite(closeTimeMs)) return;

      const isBuy = isBuyPosition(position);
      const profitValue = position.profit || 0;
      const isProfit = profitValue >= 0;
      const profitDisplay = formatProfitDisplay(position, isBuy, isPipsVisible);

      const tradeData: TradeHistoryData = {
        ticket: ticketKey,
        direction: isBuy ? "BUY" : "SELL",
        lots: position.lots || 0,
        openTime: openTimeMs,
        closeTime: closeTimeMs,
        openPrice: position.openPrice,
        closePrice: position.closePrice,
        profit: profitValue,
        symbol: position.symbol,
        digits: position.digits || 5,
        isProfit,
        profitDisplay,
      };

      const existingOverlayId = currentOverlayIds.get(ticketKey);

      if (existingOverlayId) {
        try {
          const overlay =
            chartInstanceRef.current.getOverlayById(existingOverlayId);
          if (overlay) {
            chartInstanceRef.current.overrideOverlay({
              id: existingOverlayId,
              extendData: tradeData,
            });
          }
        } catch (error) {
          console.error("Failed to update history line:", error);
        }
      } else {
        try {
          const newOverlayId = createTradeHistoryLineOverlay(
            chartInstanceRef.current,
            tradeData,
            handleTradeClick,
            handleTradeDoubleClick
          );

          if (newOverlayId) {
            currentOverlayIds.set(ticketKey, newOverlayId);
          }
        } catch (error) {
          console.error("Failed to create history line:", error);
        }
      }
    });

    currentOverlayIds.forEach((overlayId, ticket) => {
      if (!activeTickets.has(ticket)) {
        try {
          chartInstanceRef.current.removeOverlay(overlayId);
          currentOverlayIds.delete(ticket);
        } catch (error) {
          console.error(
            `Failed to remove history overlay for ticket ${ticket}:`,
            error
          );
        }
      }
    });

    if (currentSymbolRef.current === currentSymbol) {
      setOverlayIds((prev) =>
        overlayMapsEqual(prev, currentOverlayIds) ? prev : currentOverlayIds
      );
    }
  };

  useEffect(() => {
    if (!chartInstanceRef.current || !selectedSymbol || !showTradeHistory) {
      cancelAllTimeouts();
      if (!showTradeHistory) {
        removeAllTradeHistoryLines();
      }
      return;
    }

    cancelAllTimeouts();

    initTimeoutRef.current = setTimeout(() => {
      if (currentSymbolRef.current !== selectedSymbol.currency) return;

      const attemptSync = (retryCount = 0) => {
        if (currentSymbolRef.current !== selectedSymbol.currency) return;

        const dataList = chartInstanceRef.current?.getDataList();

        if (!dataList || dataList.length === 0) {
          if (retryCount < 10) {
            const delay = Math.min(100 * Math.pow(1.5, retryCount), 2000);
            retryTimeoutRef.current = setTimeout(() => {
              attemptSync(retryCount + 1);
            }, delay);
          }
          return;
        }

        syncTradeHistoryLines();
      };

      attemptSync();
    }, 100);

    return () => {
      cancelAllTimeouts();
    };
  }, [
    historyPositions,
    selectedSymbol,
    selectedTimeframe,
    showTradeHistory,
    isPipsVisible,
  ]);

  const handleTradeClick = (tradeData: TradeHistoryData) => {
    if (onTradeClick) {
      onTradeClick(tradeData);
    }
  };

  const handleTradeDoubleClick = (tradeData: TradeHistoryData) => {
    if (onTradeDoubleClick) {
      onTradeDoubleClick(tradeData);
    }
  };

  useEffect(() => {
    justResetRef.current = true;
    removeAllTradeHistoryLines();
    setOverlayIds(new Map());

    setTimeout(() => {
      justResetRef.current = false;
    }, 100);
  }, [selectedTimeframe]);

  useEffect(() => {
    cleanupTimeoutRef.current = setTimeout(() => {
      removeAllTradeHistoryLines();
    }, 50);

    return () => {
      if (cleanupTimeoutRef.current) {
        clearTimeout(cleanupTimeoutRef.current);
      }
    };
  }, [selectedSymbol]);

  useEffect(() => {
    return () => {
      cancelAllTimeouts();
      removeAllTradeHistoryLines();
    };
  }, []);

  return {
    overlayIds,
    removeAllTradeHistoryLines,
    syncTradeHistoryLines,
  };
};
